import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import * as ExcelJS from 'exceljs';
import { toast } from 'react-toastify';
import { CustomerDto } from '@/pages/Dashboard/Clientes/dto/ImportCoworkingCustomerRow';
import styles from './DataInconsistencyModal.module.css';

interface DataInconsistencyModalProps {
  open: boolean;
  onClose: () => void;
  inconsistentData: {
    customersWithoutSub?: CustomerDto[];
    customersWithInvalidEmails?: CustomerDto[];
  };
}

const EXPORT_FIELDS: (keyof CustomerDto)[] = [
  'name',
  'email',
  'phone',
  'companyDocument',
  'postalBoxNumber',
  'legalRepresentativeName',
  'legalRepresentativeDocument',
  'birthDate',
  'address',
  'productName',
  'startDate',
  'endDate',
  'affiliateId',
];

const fieldLabels: Record<keyof CustomerDto, string> = {
  name: 'Nome Empresarial',
  email: 'Email',
  phone: 'Telefone',
  companyDocument: 'CNPJ',
  postalBoxNumber: 'Caixa Postal',
  legalRepresentativeName: 'Representante Legal',
  legalRepresentativeDocument: 'CPF do Representante',
  birthDate: 'Data de Nascimento',
  address: 'Endereço',
  productName: 'Produto',
  startDate: 'Data de Início',
  endDate: 'Data de Término',
  affiliateId: 'ID do Afiliado',
};

const downloadWorkbook = async (
  rows: CustomerDto[],
  worksheetName: string,
  fileName: string,
  successMessage: string,
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'CoWonder';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(worksheetName);
  worksheet.columns = EXPORT_FIELDS.map((field) => ({
    header: fieldLabels[field] ?? field,
    key: field,
    width: 24,
  }));

  rows.forEach((customer) => {
    worksheet.addRow(customer);
  });

  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDC2626' },
    };
  });

  try {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success(successMessage);
  } catch (error) {
    console.error('Erro ao gerar o arquivo .xlsx:', error);
    toast.error('Falha ao gerar o relatório. Tente novamente.');
  }
};

const DataInconsistencyModal: React.FC<DataInconsistencyModalProps> = ({ open, onClose, inconsistentData }) => {
  const customersWithoutSub = inconsistentData.customersWithoutSub ?? [];
  const customersWithInvalidEmails = inconsistentData.customersWithInvalidEmails ?? [];

  if (!open) return null;

  const handleDownloadWithoutSub = async () => {
    if (customersWithoutSub.length === 0) {
      toast.info('Não há dados inconsistentes para baixar.');
      return;
    }

    await downloadWorkbook(
      customersWithoutSub,
      'Clientes sem inscrição',
      'clientes_sem_inscricao.xlsx',
      'Download da lista de clientes sem inscrição iniciado.',
    );
  };

  const handleDownloadInvalidEmails = async () => {
    if (customersWithInvalidEmails.length === 0) {
      toast.info('Não há clientes sem email para baixar.');
      return;
    }

    await downloadWorkbook(
      customersWithInvalidEmails,
      'Clientes sem email',
      'clientes_sem_email.xlsx',
      'Download da lista de clientes sem email iniciado.',
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <AlertTriangle size={24} className={styles.warningIcon} />
          <h2 id="data-inconsistency-modal-title" className={styles.modalTitle}>
            Inconsistência de Dados
          </h2>
          <button className={styles.closeBtn} onClick={onClose} title="Fechar">
            <X size={24} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <p id="data-inconsistency-modal-description">
            Detectamos inconsistências no cadastro de clientes. Baixe as listas abaixo para revisar os registros e corrigir os dados.
          </p>

          <ul className={styles.issueList}>
            <li className={styles.issueItem}>
              <div className={styles.issueInfo}>
                <span className={styles.issueTitle}>Clientes sem inscrição</span>
                <span className={styles.issueDescription}>
                  {customersWithoutSub.length} cliente(s) sem vínculo com produto.
                </span>
              </div>
              <button
                type="button"
                className={styles.downloadLink}
                onClick={handleDownloadWithoutSub}
                disabled={customersWithoutSub.length === 0}
                title="Baixar .xlsx de clientes sem inscrição"
              >
                Baixar .xlsx com inconsistências
              </button>
            </li>

            <li className={styles.issueItem}>
              <div className={styles.issueInfo}>
                <span className={styles.issueTitle}>Clientes sem email</span>
                <span className={styles.issueDescription}>
                  {customersWithInvalidEmails.length} cliente(s) sem email válido.
                </span>
              </div>
              <button
                type="button"
                className={styles.downloadLink}
                onClick={handleDownloadInvalidEmails}
                disabled={customersWithInvalidEmails.length === 0}
                title="Baixar .xlsx de clientes sem email válido"
              >
                Baixar .xlsx com inconsistências
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DataInconsistencyModal;
